import {
  ChildUser,
  FlexBox,
  JoonUIColor,
  Modal,
  ModalHeader,
  SPACING,
  Switch,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import DoterChatLogMessage from "./DoterChatLogMessage"
import useFlaggedConvoModalStore from "./useFlaggedConvoModalStore"
import { PREFERENCE_KEY } from "../../../../constants"
import { useDoterChatLogsQuery } from "../../../../networking/chatSessions/queries"
import { useUpdatePreferenceMutation } from "../../../../networking/families/mutations"
import { useFamilyQuery } from "../../../../networking/queries"
import { FlagType } from "../../../../types"
import { trackAnalyticEvent } from "../../../../util/analytics"
import { ANALYTIC_EVENTS } from "../../../../util/analytics"
import { getUserFromFamily } from "../../../../util/util"
import { useDoterChatStore } from "../../childUser/useDoterChatStore"
import useFamilyUserSelectStore from "../../hooks/useFamilyUserSelect"

const FlaggedConvoModal = () => {
  const { tab } = useDoterChatStore()
  const { isOpen, onClose, date, logIdToScrollTo, setLogIdToScrollTo } =
    useFlaggedConvoModalStore()
  const { selectedUserId } = useFamilyUserSelectStore()
  const { data: convo } = useDoterChatLogsQuery(date || undefined)
  const { data: family } = useFamilyQuery()
  const child = getUserFromFamily(family!, selectedUserId || 0) as
    | ChildUser
    | undefined

  const updatePreferenceMutation = useUpdatePreferenceMutation()
  const { t } = useTranslation()

  // Scroll to flagged message when it is opened / element has been rendered
  useEffect(() => {
    if (logIdToScrollTo && convo) {
      setTimeout(() => {
        const element = document.getElementById(logIdToScrollTo.toString())
        if (element) element.scrollIntoView({ behavior: "smooth" })
        setLogIdToScrollTo(null)
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logIdToScrollTo, convo])

  if (!convo || !child) return null

  const handleToggle = () => {
    if (!selectedUserId) return

    trackAnalyticEvent(ANALYTIC_EVENTS.TOGGLE_DOTER_CHAT, {
      source: "flagged_details",
      enabled: !child?.preferences.enable_doter_chat,
    })
    updatePreferenceMutation.mutate({
      childId: selectedUserId,
      key: PREFERENCE_KEY.enableDoterChat,
      value: !child?.preferences.enable_doter_chat,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      mobileFull
      style={{ width: "min(500px, 100vw)" }}
    >
      <ModalHeader
        title={t("DC_FLAGGED_CONVO_TITLE", {
          date: dayjs(date).format("MMM D"),
        })}
        onClose={onClose}
        showBorderBottom
      />
      <div
        className={`pt-4 overflow-y-auto ${
          logIdToScrollTo ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="px-6 flex flex-col gap-4">
          {[...convo]
            ?.reverse()
            .map((log, index) => <DoterChatLogMessage key={index} log={log} />)}
        </div>
        <div className="flex flex-col gap-2 py-4 px-6 mt-6 border-t border-borderPrimary">
          {tab === FlagType.RED && (
            <>
              <Typography variant="bodyBold">
                {t("DC_NOTE_FROM_JOON")}
              </Typography>
              <Typography variant="bodySmall">
                {t("DC_NOTE_DISCUSS")}
              </Typography>
              <Typography variant="bodySmall">
                {t("DC_URGENT_US")}
                <Typography variant="bodySmallBold">
                  {" "}
                  {t("DC_URGENT_US_BOLD")}
                </Typography>
                {t("DC_INTL_RESOURCES")}
                <Typography variant="bodySmallBold">
                  <a
                    href="https://blog.opencounseling.com/suicide-hotlines/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      color: JoonUIColor.text.primaryAccent,
                    }}
                  >
                    {t("DC_CHECK_HERE")}
                  </a>
                </Typography>
              </Typography>
            </>
          )}
          <FlexBox
            direction="row"
            gap={SPACING.space2}
            justify="space-between"
            align="center"
            wrap={false}
            style={{
              padding: SPACING.space3,
              background: JoonUIColor.background.lightGray,
              borderRadius: SPACING.space2,
            }}
          >
            <Typography variant="bodySmallBold">
              {t("DC_DOTER_AI_FEATURE", { name: child.name })}
            </Typography>
            <FlexBox
              direction="row"
              gap={SPACING.space2}
              wrap={false}
              align="center"
              style={{ width: "fit-content" }}
            >
              <Typography variant="body">
                {child.preferences.enable_doter_chat
                  ? t("DOTER_AI_ON")
                  : t("DOTER_AI_OFF")}
              </Typography>
              <Switch
                checked={child?.preferences.enable_doter_chat}
                onChange={handleToggle}
                name="enableDoterChat"
              />
            </FlexBox>
          </FlexBox>
          <button
            onClick={() =>
              window.open(
                "https://forms.gle/F8ieX5HPK5hk3jCt7",
                "_blank",
                "noopener,noreferrer"
              )
            }
            style={{ marginTop: SPACING.space2 }}
          >
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.text.primaryAccent}
            >
              {t("DC_SHARE_FEEDBACK")}
            </Typography>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default FlaggedConvoModal
